/**
 * Alerts Dashboard Component
 * 
 * Displays and manages system alerts with options to
 * acknowledge and resolve them.
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ShieldAlert, AlertTriangle, Bell, BellRing, 
  RefreshCcw, Eye, CheckCircle2 
} from "lucide-react";
import { AgentAvatars, AlertSeverityColors } from "./constants";

const AlertTypeLabels = {
  task_stuck: "Tarefa Travada",
  high_failure_rate: "Alta Taxa de Falhas",
  payment_recurring: "Pagamento Recorrente",
  orders_stuck: "Pedidos Travados",
  agent_overloaded: "Agente Sobrecarregado",
  system_health: "Saude do Sistema",
};

export function AlertsDashboard({ alerts, agents, onAcknowledge, onResolve, onCheckAlerts }) {
  const { alerts: alertList, counts } = alerts || { alerts: [], counts: { total: 0, critical: 0, warning: 0 } };

  return (
    <div className="space-y-6">
      {/* Alert Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-red-400">{counts.critical || 0}</p>
                <p className="text-sm text-muted-foreground">Criticos</p>
              </div>
              <ShieldAlert className="w-8 h-8 text-red-400 opacity-70" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-amber-400">{counts.warning || 0}</p>
                <p className="text-sm text-muted-foreground">Avisos</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-400 opacity-70" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-blue-400">{counts.info || 0}</p>
                <p className="text-sm text-muted-foreground">Info</p>
              </div>
              <Bell className="w-8 h-8 text-blue-400 opacity-70" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border/50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold">{counts.total || 0}</p>
                <p className="text-sm text-muted-foreground">Total Ativos</p>
              </div>
              <BellRing className="w-8 h-8 text-muted-foreground opacity-70" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onCheckAlerts}>
          <RefreshCcw className="w-4 h-4 mr-2" />
          Verificar Alertas
        </Button>
      </div>

      {/* Alert List */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BellRing className="w-5 h-5 text-amber-400" />
            Alertas Ativos
          </CardTitle>
          <CardDescription>
            Alertas gerados automaticamente pelo sistema de monitoramento
          </CardDescription>
        </CardHeader>
        <CardContent>
          {alertList.length > 0 ? (
            <div className="space-y-3">
              {alertList.map((alert) => {
                const avatar = AgentAvatars[alert.agent] || "🤖";
                
                return (
                  <div 
                    key={alert.alert_id}
                    className={`p-4 rounded-lg border ${AlertSeverityColors[alert.severity] || AlertSeverityColors.info}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className="text-2xl">{avatar}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">
                              {AlertTypeLabels[alert.type] || alert.type}
                            </Badge>
                            <Badge className={AlertSeverityColors[alert.severity]}>
                              {alert.severity}
                            </Badge>
                            {alert.acknowledged && (
                              <Badge variant="secondary" className="text-xs">
                                Reconhecido
                              </Badge>
                            )}
                          </div>
                          <p className="font-medium">{alert.title}</p>
                          <p className="text-sm text-muted-foreground mt-1">{alert.description}</p>
                          {alert.restaurant_name && (
                            <p className="text-xs text-muted-foreground mt-2">
                              Restaurante: {alert.restaurant_name}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(alert.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {!alert.acknowledged && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => onAcknowledge(alert.alert_id)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Reconhecer
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="default"
                          onClick={() => onResolve(alert.alert_id)}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Resolver
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4 opacity-50 text-green-400" />
              <p className="text-lg font-medium">Nenhum alerta ativo</p>
              <p className="text-sm mt-1">O sistema esta operando normalmente</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
